import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Clock, Users, MailPlus, ArrowRight, BarChart3, Activity, FileText, Workflow, HelpCircle,
  Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InviteDialog } from "@/components/InviteDialog";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { MODULES } from "@/lib/modules";
import { RebrandBanner } from "@/components/RebrandBanner";
import { GettingStartedChecklist } from "@/components/GettingStartedChecklist";

function StatCard({ label, value, hint, icon: Icon, testId, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass rounded-2xl p-6"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-eyebrow text-white/40">{label}</div>
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-4 w-4 text-white/90" />
        </div>
      </div>
      <div className="font-display mt-3 text-4xl font-light tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-white/45">{hint}</div>
    </motion.div>
  );
}

export default function AppHome() {
  const { user, org } = useAuth();
  const [stats, setStats] = useState({ members: 0, pending_invites: 0, ai_messages_month: 0, conversations: 0, kb_documents: 0, kb_searches_month: 0, days_left_in_trial: 0, leads_total: 0, sequences_active: 0, sequences_sent_month: 0, content_month: 0, agents_active: 0, agent_conversations_month: 0 });
  const [inviteOpen, setInviteOpen] = useState(false);

  const fetchStats = async () => {
    try {
      const [u, l, s, c, a] = await Promise.all([
        api.get("/usage/summary"),
        api.get("/leads?page=1&page_size=1").catch(() => ({ data: { total: 0 } })),
        api.get("/sequences").catch(() => ({ data: [] })),
        api.get("/content/generations?limit=200").catch(() => ({ data: [] })),
        api.get("/agents").catch(() => ({ data: [] })),
      ]);
      const active = (s.data || []).filter((x) => x.status === "active").length;
      const sentMonth = (s.data || []).reduce((sum, x) => sum + (x.stats?.sent || 0), 0);
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      const contentMonth = (c.data || []).filter((g) => new Date(g.created_at).getTime() >= monthStart).length;
      const agentsActive = (a.data || []).filter((x) => x.status === "active").length;
      const agentConvosMonth = (a.data || []).reduce((sum, x) => sum + (x.stats?.conversations || 0), 0);
      setStats({
        ...u.data,
        leads_total: l.data.total,
        sequences_active: active, sequences_sent_month: sentMonth,
        content_month: contentMonth,
        agents_active: agentsActive, agent_conversations_month: agentConvosMonth,
      });
    } catch (_) {}
  };

  useEffect(() => { fetchStats(); }, [org?.id]);

  const isTrial = org?.plan === "trial";
  const isActive = org?.subscription_status === "active";

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="text-eyebrow text-fuchsia-400">Workspace</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
          Welcome, <span className="brand-text">{user?.name?.split(" ")[0]}</span>.
        </h1>
        <p className="mt-2 max-w-xl text-white/55">
          Your team's home for AI automation. Let's get the first workflow shipped today.
        </p>
      </motion.div>

      {/* Rebrand soft-landing banner (Iter 17) — only renders when
          server gate user.should_show_rebrand_banner is true. */}
      <RebrandBanner />

      {/* Iter 20 — Getting Started checklist. Replaces the welcome-video
          surface when no platform_settings.welcome_video_url is set; if a
          video is set later, the video card lives above this checklist. */}
      <GettingStartedChecklist />

      {/* Trial banner */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
        className="glass-heavy relative mt-8 overflow-hidden rounded-2xl p-6 sm:p-8"
        data-testid="trial-banner"
      >
        <div className="absolute inset-x-0 top-0 h-0.5 brand-bg" />
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl brand-bg">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-eyebrow text-white/40">
                {isActive ? "Subscription" : "Trial status"}
              </div>
              <div className="font-display mt-1 text-2xl font-light">
                {isActive ? (
                  <>Pro — <span className="brand-text">active</span></>
                ) : (
                  <>
                    <span data-testid="trial-days-left">{stats.days_left_in_trial}</span> days left in your trial
                  </>
                )}
              </div>
              <div className="mt-1 text-sm text-white/50">
                {isActive
                  ? "Thanks for being on Pro. Manage billing in Settings."
                  : "Try every module, unlimited. No credit card required."}
              </div>
            </div>
          </div>
          <Link to="/app/settings?tab=billing" data-testid="trial-manage-billing">
            <Button className="rounded-full bg-white text-black hover:bg-zinc-200">
              {isActive ? "Manage billing" : "Upgrade to Pro"}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* What am I looking at? — lightweight onboarding accordion */}
      <Accordion type="single" collapsible className="mt-6" data-testid="home-guide-accordion">
        <AccordionItem value="guide" className="rounded-2xl border border-white/10 bg-white/[0.02] px-4">
          <AccordionTrigger className="text-left text-sm text-white/80 hover:no-underline" data-testid="home-guide-trigger">
            <span className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-fuchsia-300" />
              What am I looking at?
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4 text-sm text-white/70" data-testid="home-guide-content">
            <p>
              This is your workspace home. It shows a quick snapshot — your trial status, the number
              of leads in your pipeline, sequences running, content generated, and agents active.
              Everything here is read-only; click any tile to jump straight to that module.
            </p>
            <ul className="ml-4 list-disc space-y-1 text-white/65">
              <li><span className="text-white">Sidebar</span> — switch between modules (Leads, Sequences, Content, Agents, Chat, Knowledge Base, and more).</li>
              <li><span className="text-white">Trial banner</span> — shows days remaining or your active subscription.</li>
              <li><span className="text-white">Stats</span> — live counts of leads, active sequences, and recent activity.</li>
              <li><span className="text-white">Invite teammate</span> — bring a colleague in; they get a real email invite.</li>
            </ul>
            <p className="text-white/55">
              Each module has its own <span className="text-white">Guide</span> tab in the top-right — open it
              any time for plain-English instructions on that feature.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Stats */}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Leads" value={stats.leads_total} hint="active in pipeline"
          icon={Users} testId="stat-leads"
          accent="bg-gradient-to-br from-indigo-500/40 to-indigo-500/10 border border-indigo-500/20"
        />
        <StatCard
          label="Sequences" value={stats.sequences_active} hint={`${stats.sequences_sent_month} sends this month`}
          icon={Activity} testId="stat-sequences"
          accent="bg-gradient-to-br from-violet-500/40 to-violet-500/10 border border-violet-500/20"
        />
        <StatCard
          label="Content" value={stats.content_month} hint="generated this month"
          icon={FileText} testId="stat-content"
          accent="bg-gradient-to-br from-pink-500/40 to-pink-500/10 border border-pink-500/20"
        />
        <StatCard
          label="Agents" value={stats.agents_active} hint={`${stats.agent_conversations_month} convos · total`}
          icon={Workflow} testId="stat-agents"
          accent="bg-gradient-to-br from-fuchsia-500/40 to-fuchsia-500/10 border border-fuchsia-500/20"
        />
        <StatCard
          label="AI messages" value={stats.ai_messages_month} hint={`${stats.conversations} conversations`}
          icon={BarChart3} testId="stat-runs"
          accent="bg-gradient-to-br from-amber-500/30 to-amber-500/10 border border-amber-500/20"
        />
        <StatCard
          label="Team" value={stats.members} hint={`${stats.pending_invites} pending invites`}
          icon={MailPlus} testId="stat-members"
          accent="bg-gradient-to-br from-emerald-500/30 to-emerald-500/10 border border-emerald-500/20"
        />
      </div>

      {/* Call Performance — owner/admin only */}
      {(org?.role === "owner" || org?.role === "admin") && (
        <CallPerformanceCard />
      )}

      {/* Quick actions */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
        className="mt-8 grid gap-6 lg:grid-cols-3"
      >
        <div className="glass relative rounded-2xl p-7 lg:col-span-2">
          <div className="flex items-center gap-2 text-eyebrow text-white/40">
            <Sparkles className="h-3.5 w-3.5 text-fuchsia-400" /> Explore modules
          </div>
          <h3 className="font-display mt-2 text-2xl font-light">Nine purpose-built modules.</h3>
          <p className="mt-1 text-sm text-white/55">All shipping incrementally. Tap any to preview the roadmap.</p>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {MODULES.slice(0, 6).map((m) => (
              <Link
                key={m.slug} to={`/app/${m.slug}`}
                className="group flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 transition hover:bg-white/[0.05]"
                data-testid={`quick-module-${m.slug}`}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5">
                  <m.icon className="h-4 w-4 text-fuchsia-400" />
                </div>
                <div className="flex-1">
                  <div className="text-sm">{m.name}</div>
                  <div className="text-xs text-white/40">{m.phase}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-white/30 transition group-hover:translate-x-0.5 group-hover:text-white" />
              </Link>
            ))}
          </div>
        </div>

        <div className="glass relative rounded-2xl p-7">
          <div className="text-eyebrow text-white/40">Get your team in</div>
          <h3 className="font-display mt-2 text-2xl font-light">Invite a teammate.</h3>
          <p className="mt-1 text-sm text-white/55">
            Adds 1 seat at <span className="text-white">$9/mo</span> per teammate. No charge until your next billing cycle.
          </p>
          <Button
            onClick={() => setInviteOpen(true)}
            className="mt-6 w-full rounded-xl bg-white text-black hover:bg-zinc-200"
            data-testid="invite-teammate-cta"
          >
            <MailPlus className="mr-2 h-4 w-4" /> Invite teammate
          </Button>
          <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-xs text-white/45">
            Your current org has <span className="text-white">{stats.members}</span> members and <span className="text-white">{stats.pending_invites}</span> pending invites.
          </div>
        </div>
      </motion.div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvited={fetchStats} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Call Performance card — last 7 days. Owner/admin only.
// ──────────────────────────────────────────────────────────────────────────────

function CallPerformanceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/dashboard/call-performance?window=7d")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ total_calls: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  const total = data?.total_calls ?? 0;
  const isEmpty = total === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="glass mt-6 rounded-2xl p-7"
      data-testid="call-performance-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-eyebrow text-indigo-300">
          <Phone className="h-3.5 w-3.5" /> Call Performance · Last 7 days
        </div>
        <Link
          to="/app/leads"
          className="text-xs text-white/55 transition hover:text-white"
          data-testid="call-performance-view-leads"
        >
          View leads →
        </Link>
      </div>

      {isEmpty ? (
        <div
          className="mt-4 rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-white/55"
          data-testid="call-performance-empty"
        >
          No calls logged yet. Encourage your team to use the <span className="text-white">Log Call</span> button in Smart Leads — outcomes roll up here automatically.
        </div>
      ) : (
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          <div data-testid="call-stat-connect-rate">
            <div className="text-eyebrow text-white/40">Connect rate</div>
            <div className="font-display mt-2 text-4xl font-light tracking-tight">
              {data.connect_rate == null ? "—" : `${Math.round(data.connect_rate * 100)}%`}
            </div>
            <div className="mt-1 text-xs text-white/45">
              {data.connected_calls} of {data.total_calls} calls connected
            </div>
          </div>
          <div data-testid="call-stat-avg-duration">
            <div className="text-eyebrow text-white/40">Avg duration</div>
            <div className="font-display mt-2 text-4xl font-light tracking-tight">
              {data.avg_duration_minutes == null ? "—" : `${data.avg_duration_minutes}m`}
            </div>
            <div className="mt-1 text-xs text-white/45">across logged calls with duration</div>
          </div>
          <div data-testid="call-stat-top-outcome">
            <div className="text-eyebrow text-white/40">Top outcome</div>
            <div className="font-display mt-2 text-2xl font-light tracking-tight">
              {data.top_outcome
                ? <>{data.top_outcome.label} <span className="text-white/45 text-base">({data.top_outcome.count})</span></>
                : <span className="text-white/55">All connected</span>}
            </div>
            <div className="mt-1 text-xs text-white/45">most common non-connected outcome</div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
